import { match } from 'ts-pattern'

import { FmApi } from '.'

import { EObjectiveType } from '~/core/enums'
import { identityAny } from '~/core/utils/identity'
import { P } from '~/core/utils/p'
import { EContractStatus, EDurationID, EInviteType } from '~/features/guild/contract/enums'
import { ContractInfo, InviteDetails, InviteList, InviteListItem, SearchList } from '~/features/guild/contract/types'
import {
  GuildIncomeParams,
  GuildIncomeResponse,
  GuildIncomeTableRowResponse,
  GuildLiveIncomeParams,
  GuildLiveIncomeResponse,
  GuildLiveIncomeTableRowResponse,
} from '~/features/guild/income/types'
import type {
  GuildLiveProfitListParams,
  GuildLiveProfitResponse,
  GuildLiveProfitTableDetail,
} from '~/features/guild/profit/types'
import { GuildDataReport, GuildDataReportParams } from '~/features/guild/report/types'
import { EErrorCode } from '~/shared/middleware/error-handling'
import { Pagination } from '~/shared/types/services/pagination'
import { constant } from '~/shared/utils/constant'

export const GuildApi = FmApi.injectEndpoints({
  endpoints: (build) => ({
    getMyGuild: build.query<null | ContractInfo, void>({
      query: () => '/v2/guild/my',
      providesTags: [{ type: 'MyGuild' }],
    }),

    joinGuild: build.mutation<
      {
        state:
          | 'applied' // 成功发起申请
          | 'invited' //  已存在签约邀请
        message: string
      },
      {
        guild_id: number
        duration: EDurationID
      }
    >({
      query: (body) => ({
        url: '/v2/guild/livebackend/sign',
        body,
      }),
      invalidatesTags: ['MyGuild'],
      extraOptions: { codes: [EErrorCode.GuildAlreadyInvite] },
      transformResponse: (res) =>
        match(res)
          .with({ code: 0, info: P.string }, ({ info }) => ({
            state: 'applied' as const,
            message: info,
          }))
          .with({ code: EErrorCode.GuildAlreadyInvite, info: P.string }, ({ info }) => ({
            state: 'invited' as const,
            message: info,
          }))
          .run(),
    }),

    renewGuildContract: build.mutation<
      {
        state:
          | 'applied' // 成功发起申请
          | 'invited' // 已存在续约邀请
        message: string
      },
      { duration: EDurationID }
    >({
      query: (body) => ({
        url: '/v2/guild/livebackend/renew',
        body,
      }),
      extraOptions: { codes: [EErrorCode.GuildAlreadyInvite] },
      transformResponse: (res) =>
        match(res)
          .with({ code: 0, info: P.string }, ({ info }) => ({
            state: 'applied' as const,
            message: info,
          }))
          .with({ code: EErrorCode.GuildAlreadyInvite, info: P.string }, ({ info }) => ({
            state: 'invited' as const,
            message: info,
          }))
          .run(),
      invalidatesTags: ['MyGuild'],
    }),

    revokeGuildApply: build.mutation<
      {
        state: 'success' | 'verification_required'
        message: string
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: '/v2/guild/livebackend/revoke',
        body: { applyment_id: id },
      }),
      extraOptions: { codes: [EErrorCode.GuildIdentifyVerificationRequired] },
      transformResponse: (res) =>
        match(res)
          .with({ code: 0, info: P.string }, ({ info }) => ({
            state: 'success' as const,
            message: info,
          }))
          .with({ code: EErrorCode.GuildIdentifyVerificationRequired, info: P.string }, ({ info }) => ({
            state: 'verification_required' as const,
            message: info,
          }))
          .run(),
      invalidatesTags: ['MyGuild'],
    }),

    getGuildInviteList: build.query<
      InviteList,
      {
        p?: number
        pagesize?: number
        guild_name?: string
        user_id?: number
        type?: EInviteType
        status?: EContractStatus
      }
    >({
      query: (params) => ({
        url: '/v2/guild/livebackend/invitelist',
        params,
      }),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      transformResponse: (value: { pagination: Pagination; Datas: InviteListItem[] }) => ({
        pagination: value.pagination,
        items: value.Datas,
      }),
      providesTags: (result) => {
        const ids = [...(result?.items.map((x) => x.id) ?? []), 'PARTIAL-LIST']
        return ids.map((id) => ({ type: 'GuildInvite', id }))
      },
    }),

    getGuildInviteDetails: build.query<
      undefined | InviteDetails,
      {
        applyment_id: number
      }
    >({
      query: (params) => ({
        url: '/v2/guild/livebackend/application/detail',
        params,
      }),
      providesTags: (_res, _err, arg) => [{ type: 'GuildInvite', id: arg.applyment_id }],
      extraOptions: { codes: [EErrorCode.NoPermission] },
      transformResponse: (res) =>
        match(res).with({ code: 0, info: P.select() }, identityAny).otherwise(constant(undefined)),
    }),

    agreeGuildInvite: build.mutation<string, { applyment_id: number }>({
      query: (body) => ({
        url: '/v2/guild/livebackend/passinvite',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [{ type: 'GuildInvite', id: arg.applyment_id }],
    }),

    rejectGuildInvite: build.mutation<string, { applyment_id: number }>({
      query: (body) => ({
        url: '/v2/guild/livebackend/declineinvite',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [{ type: 'GuildInvite', id: arg.applyment_id }],
    }),

    searchGuild: build.query<SearchList, { ownerId: number }>({
      query: (params) => ({
        url: '/v2/guild/search',
        params: { s: params.ownerId },
      }),
    }),

    getUserMobile: build.query<
      | {
          state: 'success'
          mobile: string
        }
      | {
          state: 'not_bind'
        },
      void
    >({
      query: () => ({
        url: '/v2/guild/getusermobile',
      }),
      extraOptions: { codes: [EErrorCode.UserNotBindPhone] },
      transformResponse: (res) =>
        match(res)
          .with({ code: 0, info: P.string }, ({ info }) => ({ state: 'success' as const, mobile: info }))
          .otherwise(constant({ state: 'not_bind' })),
    }),

    sendCode: build.mutation<
      string,
      {
        data:
          | {
              post_type: EObjectiveType.ImportantOperation
            }
          | {
              post_type: EObjectiveType.CreateGuild
              region: string
              mobile: string
            }
        captcha?: {
          session_id: string
          sig: string
          token: string
        }
      }
    >({
      query: ({ data, captcha }) => ({
        url: '/v2/guild/sendvcode',
        body: {
          ...data,
          ...captcha,
        },
      }),
    }),

    setUserPermission: build.mutation<
      {
        state: 'success' | 'fail'
        info: string
      },
      { identify_code: string; objective_type: EObjectiveType }
    >({
      query: (body) => ({
        url: '/v2/guild/setuserpermission',
        body,
      }),
      extraOptions: { codes: [EErrorCode.IdentifyCodeError] },
      transformResponse: (res) =>
        match(res)
          .with({ code: 0, info: P.string }, ({ info }) => ({
            state: 'success' as const,
            info,
          }))
          .with({ code: EErrorCode.IdentifyCodeError, info: P.string }, ({ info }) => ({
            state: 'fail' as const,
            info,
          }))
          .run(),
    }),

    getGuildPendingCounts: build.query<void, void>({
      query: () => ({
        url: '/v2/guild/applyment/pending-count',
      }),
      providesTags: ['AdminGuild'],
    }),

    getDataReport: build.query<GuildDataReport, GuildDataReportParams>({
      query: (params) => ({
        url: '/v2/guild/data-report',
        params,
      }),
    }),

    getDataReportMenu: build.query<GuildDataReport, GuildDataReportParams>({
      query: (params) => ({
        url: '/v2/guild/data-report',
        // 接口仅在第一页返回 menu 字段，所以需要单独分离出来
        params: { ...params, p: 1 },
      }),
      providesTags: ['GuildReportMenu'],
    }),

    getGuildIncome: build.query<GuildIncomeTableRowResponse, GuildIncomeParams>({
      query: (params) => ({
        url: '/v2/guild/guildincome-v3',
        params,
      }),
      transformResponse: (res: GuildIncomeResponse) => {
        // data 的 item 中没有 id，需要加上
        const data =
          res?.data?.map((item) => ({
            ...item,
            id: item.creator_id,
          })) || []
        return { ...res, data }
      },
    }),

    getGuildIncomeMenu: build.query<GuildIncomeResponse, GuildIncomeParams>({
      query: (params) => ({
        url: '/v2/guild/guildincome-v3',
        // 接口仅在第一页返回 menu 字段，所以需要单独分离出来
        params: { ...params, p: 1 },
      }),
      providesTags: ['GuildIncomeMenu'],
    }),
    getGuildLiveIncome: build.query<GuildLiveIncomeTableRowResponse, GuildLiveIncomeParams>({
      query: (params) => ({
        url: `/v2/guild/guildliveincome-v3`,
        params,
      }),
      transformResponse: (res: GuildLiveIncomeResponse) => {
        // data 的 item 中没有 id，需要加上
        const data =
          res?.data?.map((item, index) => ({
            ...item,
            id: `${item.confirm_time}${index}`,
          })) || []
        return { ...res, data }
      },
    }),
    getGuildLiveProfitList: build.query<GuildLiveProfitTableDetail, GuildLiveProfitListParams>({
      query: (params) => ({
        url: '/v2/guild/liveincomelist-v3',
        params,
      }),
      transformResponse: (res: GuildLiveProfitResponse) => {
        // data 的 item 中没有 id，需要加上
        const data =
          res?.data?.map((item, index) => ({
            ...item,
            id: `${item.confirm_time}${index}`,
          })) || []
        return { ...res, data }
      },
    }),
    getGuildLiveTotalProfit: build.query<GuildLiveProfitTableDetail, GuildLiveProfitListParams>({
      query: (params) => ({
        url: '/v2/guild/liveincomelist-v3',
        // 接口仅在第一页返回 total 字段，所以需要单独分离出来
        params: { ...params, p: 1 },
      }),
    }),
  }),
}).endpoints
